// Every section that uses the shared module includes this file, so it can be
// executed several times per page. Scope it in a function (top-level const
// declarations would throw 'already been declared' on re-execution) and only
// create the singleton once.
(function () {
  if (window.ThemeParallax) return;

  const PARALLAX_DRIFT = 'drift';
  const PARALLAX_CROSSFADE = 'crossfade-stack';
  const PARALLAX_FADE_IN = 'fade-in';

  // Factory — called once; result stored on window.ThemeParallax
  function createParallax() {
    const registry = new Map(); // element -> { config, cached }
    const visibleSet = new Set(); // elements currently intersecting the viewport
    let rafId = null;
    // Window size, read on resize (refreshAll) rather than every frame
    let viewportHeight = window.innerHeight;
    let viewportWidth = window.innerWidth;
    let resizeTimer = null;

    // prefers-reduced-motion is deliberately not honoured (owner decision; see CLAUDE.md)

    // Single observer with a generous margin so transforms begin just before elements reach the fold
    const observer = new IntersectionObserver(onIntersect, { rootMargin: '200px 0px' });

    function onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleSet.add(entry.target);
          startLoop();
        } else {
          visibleSet.delete(entry.target);
          if (visibleSet.size === 0) stopLoop();
        }
      });
    }

    // rAF loop — pauses automatically when no registered element wants to animate
    function loop() {
      if (visibleSet.size === 0) {
        rafId = null;
        return;
      }

      const scrollY = window.scrollY;
      let anyAnimated = false;

      visibleSet.forEach((el) => {
        const entry = registry.get(el);
        if (!entry) return;
        const { config, cached } = entry;
        if (!isActive(config)) return;
        anyAnimated = true;
        tick(el, config, cached, scrollY);
      });

      // Idle to a stop when nothing visible should animate (e.g. every visible
      // element is below its disableBelow width).
      if (!anyAnimated) {
        rafId = null;
        return;
      }

      rafId = requestAnimationFrame(loop);
    }

    function startLoop() {
      if (rafId !== null || visibleSet.size === 0) return;
      rafId = requestAnimationFrame(loop);
    }

    function stopLoop() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function tick(el, config, cached, scrollY) {
      const { type, axis = 'y', range = [-30, 30], onProgress } = config;
      const { elementTop, elementHeight } = cached;

      // 0 = element entering viewport from bottom, 1 = element exiting from top
      const progress = clamp(
        (scrollY + viewportHeight - elementTop) / (viewportHeight + elementHeight),
        0,
        1
      );

      if (type === PARALLAX_DRIFT) {
        const offset = range[0] + (range[1] - range[0]) * progress;
        el.style.transform =
          axis === 'x'
            ? `translate3d(${offset}px, 0, 0)`
            : `translate3d(0, ${offset}px, 0)`;
      } else if (type === PARALLAX_CROSSFADE || type === PARALLAX_FADE_IN) {
        if (typeof onProgress === 'function') onProgress(progress);
      }
    }

    // Returns false when the effect should be skipped for the current viewport width
    function isActive(config) {
      const threshold = config.disableBelow ?? 768;
      return viewportWidth >= threshold || config.mobileSpeed !== undefined;
    }

    function cacheLayout(el) {
      const rect = el.getBoundingClientRect();
      return { elementTop: rect.top + window.scrollY, elementHeight: rect.height };
    }

    function refreshAll() {
      viewportHeight = window.innerHeight;
      viewportWidth = window.innerWidth;
      registry.forEach((entry, el) => {
        entry.cached = cacheLayout(el);
      });
      // A resize can re-activate an element (e.g. a viewport-width threshold
      // crossing). loop() self-stops when nothing should animate, so kick it
      // here; it's a no-op if already running or if nothing is animatable.
      if (visibleSet.size > 0) startLoop();
    }

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refreshAll, 150);
    }

    window.addEventListener('resize', onResize, { passive: true });

    return {
      register(element, config) {
        if (registry.has(element)) return;
        // will-change only on drift elements — crossfade/fade-in types handle their own elements
        if (config.type === PARALLAX_DRIFT) element.style.willChange = 'transform';
        registry.set(element, { config, cached: cacheLayout(element) });
        observer.observe(element);
      },

      unregister(element) {
        if (!registry.has(element)) return;
        observer.unobserve(element);
        const { config } = registry.get(element);
        registry.delete(element);
        visibleSet.delete(element);
        if (config.type === PARALLAX_DRIFT) {
          element.style.transform = '';
          element.style.willChange = '';
        }
        if (visibleSet.size === 0) stopLoop();
      },
    };
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  window.ThemeParallax = createParallax();
})();
